/** Formatação localizada de datas, horas e números vindos da API.
 *  Instantes (ISO com hora) são SEMPRE renderizados no fuso de São Paulo, não no
 *  fuso do navegador — ver shared/datetime.ts. */
import { SP_TZ } from './datetime'
import i18n from '../core/i18n/i18n'

const LOCALES = { pt: 'pt-BR', en: 'en-US', es: 'es-ES' } as const

function locale(): string {
  const resolved = i18n.resolvedLanguage ?? i18n.language
  return LOCALES[resolved as keyof typeof LOCALES] ?? LOCALES.pt
}

/** Valor só de data, `YYYY-MM-DD` sem hora. `new Date('2026-09-15')` lê isso como
 *  meia-noite UTC, e em São Paulo vira 14/09 21:00: foi o que a coluna Escalado em
 *  do painel de Dispositivos mostrou para toda prancha em 15/09/2026, porque o
 *  início da viagem de máquina é Date, sem hora. Data pura sai por `fmtDate`, que
 *  não desloca, e sem hora inventada. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/
const DATE_ONLY_PARTS = /^(\d{4})-(\d{2})-(\d{2})$/

export function isDateOnly(v: string | null | undefined): boolean {
  return v != null && DATE_ONLY.test(String(v))
}

/** Data (aceita 'YYYY-MM-DD' sem shift de fuso e ISO com hora).
 *
 *  Só a data PURA lê o dia direto do texto. O instante com hora sai pelo fuso de
 *  São Paulo, como as outras funções deste arquivo: até 13/10/2026 o regex não era
 *  ancorado e lia o prefixo do ISO, que o servidor manda em UTC, e a carga
 *  programada para 16/10 às 21:10 aparecia na Frente como 17/10 21:10, com o dia
 *  da semana e o campo de edição dizendo 16/10. */
export function fmtDate(v: string | null | undefined): string {
  if (!v) return '—'
  const s = String(v)
  const m = s.match(DATE_ONLY_PARTS)
  if (m) {
    const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
    return date.toLocaleDateString(locale(), { timeZone: 'UTC' })
  }
  const d = new Date(s)
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString(locale(), { timeZone: SP_TZ })
}

/** Hora curta HH:MM (aceita 'HH:MM:SS' ou ISO com hora). */
export function fmtTime(v: string | null | undefined): string {
  if (!v) return '—'
  const s = String(v)
  const t = s.match(/(\d{2}):(\d{2})/)
  if (s.includes('T')) {
    const d = new Date(s)
    if (!isNaN(d.getTime())) return d.toLocaleTimeString(locale(), { hour: '2-digit', minute: '2-digit', timeZone: SP_TZ })
  }
  return t ? `${t[1]}:${t[2]}` : '—'
}

/** Data + hora curtas. */
export function fmtDateTime(v: string | null | undefined): string {
  if (!v) return '—'
  if (isDateOnly(v)) return fmtDate(v)
  const d = new Date(String(v))
  if (isNaN(d.getTime())) return fmtDate(v)
  return d.toLocaleString(locale(), { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: SP_TZ })
}

/** Data + hora com ano de 2 dígitos, sem vírgula: "17/07/26 12:15". Sempre em São Paulo. */
export function fmtDateTimeShort(v: string | null | undefined): string {
  if (!v) return '—'
  if (isDateOnly(v)) return fmtDate(v)
  const d = new Date(String(v))
  if (isNaN(d.getTime())) return fmtDate(v)
  return d
    .toLocaleString(locale(), { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: SP_TZ })
    .replace(', ', ' ')
}

/** Data + hora com ano completo, sem vírgula: "23/07/2026 19:57". Sempre em São Paulo. */
export function fmtDateTimeFull(v: string | null | undefined): string {
  if (!v) return '—'
  if (isDateOnly(v)) return fmtDate(v)
  const d = new Date(String(v))
  if (isNaN(d.getTime())) return fmtDate(v)
  return d
    .toLocaleString(locale(), { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: SP_TZ })
    .replace(', ', ' ')
}

/** Data crua da ATS ("2026-08-12 21:41:00") → "12/08/2026 21:41". Sem parse de
 *  Date: a ATS já manda hora local, então reformatar o texto evita shift de fuso. */
export function fmtDataAts(v: string | null | undefined): string {
  if (!v) return ''
  const m = String(v).match(/(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/)
  return m ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}` : String(v)
}

/** Número no idioma atual (milhar/decimal). */
export function fmtNum(v: number | null | undefined, digits = 0): string {
  if (v == null) return '—'
  return v.toLocaleString(locale(), { minimumFractionDigits: digits, maximumFractionDigits: digits })
}

/** Moeda BRL no idioma atual (valores da operação são sempre em reais). */
export function fmtCurrency(v: number | null | undefined): string {
  if (v == null) return '—'
  return v.toLocaleString(locale(), { style: 'currency', currency: 'BRL' })
}

/** Distância: a API traz METROS inteiros, a tela mostra km com 2 casas
 *  ("12,34 km"). null é "não foi possível medir" e vira travessão — 0 é medida
 *  real ("0,00 km") e os dois nunca se confundem. */
export function fmtKm(m: number | null | undefined): string {
  if (m == null) return '—'
  return `${fmtNum(m / 1000, 2)} km`
}

/** Duração compacta desde `from` até `now` (ex.: "38m", "2h 15m", "3d 4h"). Vazio se inválido. */
export function fmtDuration(from: string | null | undefined, now: number = Date.now()): string {
  if (!from) return ''
  const t = new Date(String(from)).getTime()
  if (isNaN(t)) return ''
  const min = Math.max(0, Math.floor((now - t) / 60_000))
  if (min < 1) return String(i18n.t('format.now', { defaultValue: 'agora' }))
  if (min < 60) return `${min}m`
  const h = Math.floor(min / 60)
  if (h < 24) return `${h}h ${String(min % 60).padStart(2, '0')}m`
  return `${Math.floor(h / 24)}d ${h % 24}h`
}

/** "Há quanto tempo" enxuto p/ apresentação: "agora", "45min", "5h", "3d". Vazio se inválido. */
export function fmtAgo(from: string | null | undefined, now: number = Date.now()): string {
  if (!from) return ''
  const t = new Date(String(from)).getTime()
  if (isNaN(t)) return ''
  const min = Math.max(0, Math.floor((now - t) / 60_000))
  if (min < 1) return String(i18n.t('format.now', { defaultValue: 'agora' }))
  if (min < 60) return `${min}min`
  const h = Math.floor(min / 60)
  if (h < 24) return `${h}h`
  return `${Math.floor(h / 24)}d`
}
